import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  HelpCircle,
  Trash2,
  Clock,
  RefreshCcw,
  CheckCircle,
  Search,
  MessageSquare,
  AlertCircle,
  Loader2,
  Info,
  ChevronRight,
  MoreVertical,
  Bell
} from "lucide-react";
import { useStreams } from "../hooks/useStreams";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";

interface Question {
  id: string;
  question: string;
  createdAt: string;
}

interface StreamData {
  streamName: string;
  uid: string;
}

const API_BASE_URL = "https://api.deafassistant.com/question";
const POLL_INTERVALS = {
  FAST: 1000,
  MEDIUM: 4000,
  SLOW: 8000,
} as const;

const QuestionsPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [streamData, setStreamData] = useState<StreamData | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string>("");
  const [pollingStatus, setPollingStatus] = useState<string>("Initializing...");
  const [isDeleting, setIsDeleting] = useState<Set<string>>(new Set());
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newQuestionIds, setNewQuestionIds] = useState<Set<string>>(new Set());

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const knownQuestionIdsRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);
  const currentPollIntervalRef = useRef<number>(POLL_INTERVALS.FAST);

  const updatePollInterval = useCallback((newInterval: number) => {
    currentPollIntervalRef.current = newInterval;
  }, []);

  const { selected, loading: streamsLoading } = useStreams();
  useEffect(() => {
    if (!user || streamsLoading) return;
    if (selected) {
      setStreamData({ streamName: selected.name, uid: user.uid });
      setError("");
    } else {
      setError("No data available.");
      setIsInitialLoad(false); // no stream, so nothing will be fetched to clear the spinner
    }
  }, [user, selected, streamsLoading]);

  const formatTimeAgo = useCallback((creationTimeString: string): string => {
    const cleanDateString = creationTimeString.substring(0, 23);
    const pastDate = new Date(cleanDateString);
    if (isNaN(pastDate.getTime())) return "Invalid time";
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - pastDate.getTime()) / 1000);
    if (diffInSeconds < 60) return "just now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return pastDate.toLocaleDateString();
  }, []);

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const updatePollingStatus = useCallback(() => {
    setPollingStatus(`Syncing every ${currentPollIntervalRef.current / 1000}s`);
  }, []);

  const fetchQuestions = useCallback(async () => {
    if (!streamData?.streamName || !streamData?.uid || !isMountedRef.current) return;
    setIsRefreshing(true);

    try {
      const url = new URL(`${API_BASE_URL}/GetStreamQuestions`);
      url.search = new URLSearchParams({
        streamName: streamData.streamName,
        uid: streamData.uid,
      }).toString();

      const response = await fetch(url);
      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const newQuestions: Question[] = await response.json();
      if (!isMountedRef.current) return;

      const currentIds = new Set(newQuestions.map((q) => q.id));
      const previousIds = knownQuestionIdsRef.current;
      const newIds = new Set(Array.from(currentIds).filter((id) => !previousIds.has(id)));

      if (newIds.size > 0) {
        setNewQuestionIds(newIds);
        setTimeout(() => setNewQuestionIds(new Set()), 2000);
      }

      const hasNewData = newQuestions.length > knownQuestionIdsRef.current.size;
      setQuestions(newQuestions);
      knownQuestionIdsRef.current = currentIds;

      if (hasNewData) updatePollInterval(POLL_INTERVALS.FAST);
      else if (currentPollIntervalRef.current === POLL_INTERVALS.FAST) updatePollInterval(POLL_INTERVALS.MEDIUM);
      else updatePollInterval(POLL_INTERVALS.SLOW);

      setError("");
      updatePollingStatus();
      if (isInitialLoad) setIsInitialLoad(false);
    } catch (err) {
      console.error("Polling failed:", err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Connection lost");
        setPollingStatus("Disconnected");
        if (isInitialLoad) setIsInitialLoad(false);
      }
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  }, [streamData, isInitialLoad, updatePollingStatus, updatePollInterval]);

  const scheduleNextPoll = useCallback(() => {
    clearPollingInterval();
    intervalRef.current = setTimeout(() => fetchQuestions(), currentPollIntervalRef.current);
    updatePollingStatus();
  }, [fetchQuestions, clearPollingInterval, updatePollingStatus]);

  useEffect(() => {
    if (!streamData || error) {
      clearPollingInterval();
      return;
    }
    fetchQuestions();
    return () => clearPollingInterval();
  }, [streamData, error, fetchQuestions, clearPollingInterval]);

  useEffect(() => {
    if (!isInitialLoad && !error && streamData) scheduleNextPoll();
  }, [questions, isInitialLoad, error, streamData, scheduleNextPoll]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  const handleDeleteQuestion = useCallback(async (questionId: string) => {
    if (!streamData) return;
    setIsDeleting((prev) => new Set(prev).add(questionId));

    try {
      const url = new URL(`${API_BASE_URL}/DelQuestion`);
      url.search = new URLSearchParams({
        streamName: streamData.streamName,
        uid: streamData.uid,
        id: questionId,
      }).toString();

      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Delete failed");
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      knownQuestionIdsRef.current.delete(questionId);
    } catch (err) {
      console.error("Failed to delete question:", err);
      alert("Action failed.");
    } finally {
      setIsDeleting((prev) => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  }, [streamData]);

  const handleClearAll = useCallback(async () => {
    if (!streamData) return;
    if (!window.confirm(t("questions.clearConfirm"))) return;

    setIsClearingAll(true);
    const url = new URL(`${API_BASE_URL}/DelStreamQuestions`);
    url.search = new URLSearchParams({
      streamName: streamData.streamName,
      uid: streamData.uid,
    }).toString();

    try {
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Clear failed");
      setQuestions([]);
      knownQuestionIdsRef.current.clear();
    } catch (err) {
      console.error("Failed to clear questions:", err);
      alert("Action failed.");
    } finally {
      setIsClearingAll(false);
    }
  }, [streamData, t]);

  const filteredQuestions = questions.filter(q =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading || isInitialLoad) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="questions" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("questions.title")}
                </h1>
                <div className="flex items-center gap-4 mt-2">
                  <p className="text-slate-500 text-lg">
                    Manage attendee questions for <span className="font-bold text-slate-700">{streamData?.streamName}</span>
                  </p>
                  <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-slate-100 shadow-sm">
                    <RefreshCcw size={14} className={`text-blue-500 ${isRefreshing ? "animate-spin" : ""}`} />
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{pollingStatus}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                {questions.length > 0 && (
                  <button onClick={handleClearAll} disabled={isClearingAll} className="btn-danger flex items-center gap-2 group">
                    {isClearingAll ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} className="text-red-400 group-hover:text-red-600 transition-colors" />}
                    {t("questions.clearAll")}
                  </button>
                )}
              </div>
            </header>

            {/* Notification Bar */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-6 text-white shadow-xl shadow-blue-200/50 flex flex-col md:flex-row items-center gap-6 relative overflow-hidden group">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0">
                <Bell className="w-8 h-8 animate-bounce" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold mb-1">Real-time Q&A Active</h3>
                <p className="text-blue-100 max-w-2xl">
                  Attendees can ask questions from the stream page. New questions will appear here instantly. Mark them as answered to keep your queue clean.
                </p>
              </div>
              <div className="absolute top-0 right-0 p-8 opacity-10 translate-x-1/4 -translate-y-1/4 group-hover:scale-110 transition-transform duration-1000">
                <MessageSquare size={200} />
              </div>
            </div>

            {/* Search and Stats */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-3 bg-white rounded-3xl p-4 shadow-sm border border-slate-100">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex items-center justify-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <MessageSquare size={20} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900 leading-none">{questions.length}</div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Queue</div>
                </div>
              </div>
            </div>

            {/* Questions List */}
            {filteredQuestions.length === 0 ? (
              <div className="bg-white rounded-[3rem] p-20 text-center shadow-sm border border-slate-100">
                <div className="w-24 h-24 bg-slate-50 rounded-[2.5rem] flex items-center justify-center text-slate-300 mx-auto mb-6">
                  <HelpCircle size={48} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Queue is empty</h3>
                <p className="text-slate-500 max-w-sm mx-auto">
                  {searchQuery ? "No matches found for your search." : "Ready for the next set of questions! They will appear here as soon as attendees ask them."}
                </p>
              </div>
            ) : (
              <div className="grid gap-6">
                {filteredQuestions.map((q, index) => {
                  const isNew = newQuestionIds.has(q.id);
                  const isBeingDeleted = isDeleting.has(q.id);

                  return (
                    <div
                      key={q.id}
                      className={`
                        group bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-blue-100 transition-all duration-500 flex flex-col md:flex-row gap-6
                        ${isBeingDeleted ? "opacity-40 scale-95 pointer-events-none" : "opacity-100 scale-100"}
                        ${isNew ? "ring-2 ring-blue-500 ring-offset-4" : ""}
                      `}
                    >
                      <div className="shrink-0 flex md:flex-col items-center justify-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-bold group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                          {index + 1}
                        </div>
                        <div className="h-px w-8 bg-slate-100 md:h-8 md:w-px" />
                      </div>

                      <div className="flex-1 space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
                            <Clock size={14} />
                            {formatTimeAgo(q.createdAt)}
                          </div>
                          {isNew && (
                            <div className="px-2 py-0.5 bg-blue-100 text-blue-600 text-[10px] font-bold rounded-full uppercase tracking-widest animate-pulse">
                              New
                            </div>
                          )}
                        </div>
                        <p className="text-slate-800 text-xl font-medium leading-relaxed">
                          {q.question}
                        </p>
                      </div>

                      <div className="shrink-0 flex items-center justify-center md:flex-col gap-3 border-t md:border-t-0 md:border-l border-slate-50 pt-4 md:pt-0 md:pl-8">
                        <button
                          onClick={() => handleDeleteQuestion(q.id)}
                          disabled={isBeingDeleted}
                          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-200 transition-all group/btn"
                        >
                          {isBeingDeleted ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle size={20} className="group-hover/btn:scale-110 transition-transform" />}
                          Answered
                        </button>
                        {/* <button className="p-3 text-slate-400 hover:text-slate-600 transition-colors">
                          <MoreVertical size={20} />
                        </button> */}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default QuestionsPage;
